"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PriceCalculator = void 0;
const legacy_enums_1 = require("@daytrip/legacy-enums");
const utils_1 = require("@daytrip/utils");
const getVehicleTypeFeeCoefficient_1 = require("../utils/getVehicleTypeFeeCoefficient");
class PriceCalculator {
    static calculateFeeFromPrice(price, feeCoefficient) {
        return price / feeCoefficient - price;
    }
    static calculateWaitingPriceFromDuration(duration, driverHourlyPrice) {
        return (duration / 60) * driverHourlyPrice;
    }
    static calculatePriceAndFeeFromValue(value, feeCoefficient) {
        const price = Math.round(value * feeCoefficient);
        const fee = Math.round(value - price);
        return { price, fee };
    }
    static multiplyByCoefficient(value, coefficient) {
        return (0, utils_1.isUndefinedOrNull)(coefficient) ? value : value * coefficient;
    }
    static multiplyByCoefficientAndRound(value, coefficient) {
        return Math.round(this.multiplyByCoefficient(value, coefficient));
    }
    static calculateRouteVehiclePriceFromDistance(distance, amortisationPrice, consumptionPrice) {
        return distance * amortisationPrice * 2 + distance * consumptionPrice * 2;
    }
    static calculateRouteGuidePriceFromDuration(duration, driverHourlyPrice) {
        return (duration / 60) * driverHourlyPrice * 2;
    }
    static calculateVehicleDetourPriceFromDistance(distance, amortisationPrice, consumptionPrice) {
        return distance * (amortisationPrice + consumptionPrice);
    }
    static calculateOrderPrice(order, discounts, countTravelAgentDiscount = true) {
        let priceDiscount = 0;
        if ((0, utils_1.isNotUndefinedOrNull)(discounts) && discounts.length > 0) {
            priceDiscount = discounts
                // for cases when full price is displayed
                .filter((d) => countTravelAgentDiscount || d.type !== legacy_enums_1.DiscountType.TravelAgent)
                .filter((d) => (0, utils_1.isUndefinedOrNull)(d.priceDiscountCoefficient) || d.priceDiscountCoefficient === 0)
                .reduce((amount, discount) => amount + discount.price, 0);
        }
        let priceDiscountCoefficient = 0;
        if ((0, utils_1.isNotUndefinedOrNull)(discounts) && discounts.length > 0) {
            priceDiscountCoefficient = discounts
                // for cases when full price is displayed
                .filter((d) => countTravelAgentDiscount || d.type !== legacy_enums_1.DiscountType.TravelAgent)
                .filter((d) => (0, utils_1.isNotUndefinedOrNull)(d.priceDiscountCoefficient))
                .reduce((total, discount) => total + (discount.priceDiscountCoefficient ?? 0), 0);
        }
        const price = order.vehicles.reduce((sum, vehicleType) => {
            const vehiclePrice = this.calculateVehiclePrice(order, vehicleType);
            const vehicleFee = this.calculateVehicleFee(order, vehicleType);
            const totalPrice = vehiclePrice + vehicleFee;
            const totalPriceDiscount = Math.round(totalPrice * priceDiscountCoefficient);
            return sum + Math.round(vehiclePrice - totalPriceDiscount);
        }, 0);
        return Math.round(price - priceDiscount);
    }
    static calculateVehiclePrice(order, vehicleType) {
        const baseVehicleTypePriceFee = order.vehicleTypesPricesFees.find((vtpf) => vtpf.vehicleType === vehicleType);
        if (!baseVehicleTypePriceFee) {
            throw new Error(`Order ${order._id} does not have pricing defined for vehicle type ${vehicleType}`);
        }
        let priceAdjustmentCoefficient = order.priceAdjustmentCoefficient;
        if (order.vehicleCoefficients?.length) {
            const vehicleCoefficient = order.vehicleCoefficients.find((coefficient) => coefficient.vehicleType === vehicleType);
            if (vehicleCoefficient) {
                priceAdjustmentCoefficient = vehicleCoefficient.priceAdjustmentCoefficient;
            }
        }
        const basePrice = PriceCalculator.multiplyByCoefficientAndRound(baseVehicleTypePriceFee.price, priceAdjustmentCoefficient);
        const locationsPrice = order.contentLocations.reduce((oclSum, ocl) => oclSum + PriceCalculator.calculateContentLocationPrice(ocl, vehicleType), 0);
        const customLocationsPrice = order.customLocations.reduce((oclSum, ocl) => {
            const oclVehicleTypePriceFee = ocl.vehicleTypesPricesFees.find((vtpf) => vtpf.vehicleType === vehicleType);
            return oclSum + (oclVehicleTypePriceFee?.driverWaitingPrice ?? 0) + (oclVehicleTypePriceFee?.price ?? 0);
        }, 0);
        return Math.round(order.tollPrice * 2 + order.additionalPrice + basePrice + locationsPrice + customLocationsPrice);
    }
    static calculateBasePriceFromVehiclePrice(order, vehicleType, targetVehiclePrice) {
        const baseVehicleTypePriceFee = order.vehicleTypesPricesFees.find((vtpf) => vtpf.vehicleType === vehicleType);
        if (!baseVehicleTypePriceFee) {
            throw new Error(`Order ${order._id} does not have pricing defined for vehicle type ${vehicleType}`);
        }
        let priceAdjustmentCoefficient = order.priceAdjustmentCoefficient;
        if (order.vehicleCoefficients?.length) {
            const vehicleCoefficient = order.vehicleCoefficients.find((coefficient) => coefficient.vehicleType === vehicleType);
            if (vehicleCoefficient) {
                priceAdjustmentCoefficient = vehicleCoefficient.priceAdjustmentCoefficient;
            }
        }
        const locationsPrice = order.contentLocations.reduce((oclSum, ocl) => oclSum + PriceCalculator.calculateContentLocationPrice(ocl, vehicleType), 0);
        const customLocationsPrice = order.customLocations.reduce((oclSum, ocl) => {
            const oclVehicleTypePriceFee = ocl.vehicleTypesPricesFees.find((vtpf) => vtpf.vehicleType === vehicleType);
            return oclSum + (oclVehicleTypePriceFee?.driverWaitingPrice ?? 0) + (oclVehicleTypePriceFee?.price ?? 0);
        }, 0);
        // Calculate the adjusted base price by subtracting all additional components
        const adjustedBasePrice = Math.round(targetVehiclePrice - order.tollPrice * 2 - order.additionalPrice - locationsPrice - customLocationsPrice);
        // Reverse the coefficient multiplication to get the original base price
        if ((0, utils_1.isUndefinedOrNull)(priceAdjustmentCoefficient) || priceAdjustmentCoefficient === 0) {
            return adjustedBasePrice;
        }
        return Math.round(adjustedBasePrice / priceAdjustmentCoefficient);
    }
    static calculateOrderFee(order, discounts, countTravelAgentDiscount = true) {
        let feeDiscount = 0;
        if (discounts && discounts.length > 0) {
            feeDiscount = discounts
                // for cases when full price is displayed
                .filter((d) => countTravelAgentDiscount || d.type !== legacy_enums_1.DiscountType.TravelAgent)
                .filter((discount) => {
                const feeDiscountCoefficientDoesNotExist = (0, utils_1.isUndefinedOrNull)(discount.feeDiscountCoefficient) || discount.feeDiscountCoefficient === 0;
                return feeDiscountCoefficientDoesNotExist;
            })
                .reduce((amount, discount) => amount + discount.fee, 0);
        }
        let feeDiscountCoefficient = 0;
        if ((0, utils_1.isNotUndefinedOrNull)(discounts) && discounts.length > 0) {
            feeDiscountCoefficient = discounts
                // for cases when full price is displayed
                .filter((d) => countTravelAgentDiscount || d.type !== legacy_enums_1.DiscountType.TravelAgent)
                .filter((d) => (0, utils_1.isNotUndefinedOrNull)(d.feeDiscountCoefficient))
                .reduce((total, discount) => total + (discount.feeDiscountCoefficient ?? 0), 0);
        }
        const fee = order.vehicles.reduce((sum, vehicleType) => {
            const vehiclePrice = this.calculateVehiclePrice(order, vehicleType);
            const vehicleFee = this.calculateVehicleFee(order, vehicleType);
            const totalPrice = vehiclePrice + vehicleFee;
            const totalFeeDiscount = Math.round(totalPrice * feeDiscountCoefficient);
            return sum + Math.round(vehicleFee - totalFeeDiscount);
        }, 0);
        return Math.round(fee - feeDiscount);
    }
    static calculateVehicleFee(order, vehicleType) {
        const baseVehicleTypePriceFee = order.vehicleTypesPricesFees.find((vtpf) => vtpf.vehicleType === vehicleType);
        if (!baseVehicleTypePriceFee) {
            throw new Error(`Order ${order._id} does not have pricing defined for vehicle type ${vehicleType}`);
        }
        let feeAdjustmentCoefficient = order.feeAdjustmentCoefficient ?? order.priceAdjustmentCoefficient;
        if (order.vehicleCoefficients?.length) {
            const vehicleCoefficient = order.vehicleCoefficients.find((coefficient) => coefficient.vehicleType === vehicleType);
            if (vehicleCoefficient) {
                feeAdjustmentCoefficient = vehicleCoefficient.feeAdjustmentCoefficient;
            }
        }
        const baseFee = PriceCalculator.multiplyByCoefficientAndRound(baseVehicleTypePriceFee.fee, feeAdjustmentCoefficient);
        const locationsFee = order.contentLocations.reduce((oclSum, ocl) => oclSum + PriceCalculator.calculateContentLocationFee(ocl, vehicleType), 0);
        const customLocationsFee = order.customLocations.reduce((oclSum, ocl) => {
            const oclVehicleTypePriceFee = ocl.vehicleTypesPricesFees.find((vtpf) => vtpf.vehicleType === vehicleType);
            return oclSum + (oclVehicleTypePriceFee?.driverWaitingFee ?? 0) + (oclVehicleTypePriceFee?.fee ?? 0);
        }, 0);
        return Math.round(order.tollFee * 2 + order.additionalFee + baseFee + locationsFee + customLocationsFee);
    }
    static calculateBaseFeeFromVehicleFee(order, vehicleType, targetVehicleFee) {
        const baseVehicleTypePriceFee = order.vehicleTypesPricesFees.find((vtpf) => vtpf.vehicleType === vehicleType);
        if (!baseVehicleTypePriceFee) {
            throw new Error(`Order ${order._id} does not have pricing defined for vehicle type ${vehicleType}`);
        }
        let feeAdjustmentCoefficient = order.feeAdjustmentCoefficient ?? order.priceAdjustmentCoefficient;
        if (order.vehicleCoefficients?.length) {
            const vehicleCoefficient = order.vehicleCoefficients.find((coefficient) => coefficient.vehicleType === vehicleType);
            if (vehicleCoefficient) {
                feeAdjustmentCoefficient = vehicleCoefficient.feeAdjustmentCoefficient;
            }
        }
        const locationsFee = order.contentLocations.reduce((oclSum, ocl) => oclSum + PriceCalculator.calculateContentLocationFee(ocl, vehicleType), 0);
        const customLocationsFee = order.customLocations.reduce((oclSum, ocl) => {
            const oclVehicleTypePriceFee = ocl.vehicleTypesPricesFees.find((vtpf) => vtpf.vehicleType === vehicleType);
            return oclSum + (oclVehicleTypePriceFee?.driverWaitingFee ?? 0) + (oclVehicleTypePriceFee?.fee ?? 0);
        }, 0);
        // Calculate the adjusted base fee by subtracting all additional components
        const adjustedBaseFee = Math.round(targetVehicleFee - order.tollFee * 2 - order.additionalFee - locationsFee - customLocationsFee);
        // Reverse the coefficient multiplication to get the original base fee
        if ((0, utils_1.isUndefinedOrNull)(feeAdjustmentCoefficient) || feeAdjustmentCoefficient === 0) {
            return adjustedBaseFee;
        }
        return Math.round(adjustedBaseFee / feeAdjustmentCoefficient);
    }
    static calculateContentLocationPrice(location, vehicleType) {
        const oclVehicleTypePriceFee = location.vehicleTypesPricesFees.find((vtpf) => vtpf.vehicleType === vehicleType);
        if (!oclVehicleTypePriceFee) {
            throw new Error(`Content location ${location.locationId} does not have pricing defined for vehicle type ${vehicleType}`);
        }
        return Math.round(location.entrancePrice +
            location.parkingPrice +
            location.tollPrice * 2 +
            location.additionalPrice +
            oclVehicleTypePriceFee.driverWaitingPrice +
            oclVehicleTypePriceFee.price);
    }
    // TODO: Remove any when we move legacy to api
    static calculateStopOfferPrice(stopOffer, vehicleType) {
        const oclVehicleTypePriceFee = stopOffer.vehicles.find((vtpf) => vtpf.vehicleType === vehicleType);
        if (!oclVehicleTypePriceFee) {
            throw new Error(`Content location ${stopOffer.locationId} does not have pricing defined for vehicle type ${vehicleType}`);
        }
        return Math.round(oclVehicleTypePriceFee.totalPrice.getBasePrice());
    }
    static calculateContentLocationFee(location, vehicleType) {
        const oclVehicleTypePriceFee = location.vehicleTypesPricesFees.find((vtpf) => vtpf.vehicleType === vehicleType);
        if (!oclVehicleTypePriceFee) {
            throw new Error(`Content location ${location.locationId} does not have pricing defined for vehicle type ${vehicleType}`);
        }
        return Math.round(location.entranceFee +
            location.parkingFee +
            location.tollFee * 2 +
            location.additionalFee +
            oclVehicleTypePriceFee.driverWaitingFee +
            oclVehicleTypePriceFee.fee);
    }
    // TODO: Remove any when we move legacy to api
    static calculateStopOfferFee(stopOffer, vehicleType) {
        const oclVehicleTypePriceFee = stopOffer.vehicles.find((vtpf) => vtpf.vehicleType === vehicleType);
        if (!oclVehicleTypePriceFee) {
            throw new Error(`Content location ${stopOffer.locationId} does not have pricing defined for vehicle type ${vehicleType}`);
        }
        return Math.round(oclVehicleTypePriceFee.totalPrice.fee);
    }
    static minusPercent(value, percent) {
        return value * (1 - percent / 100);
    }
    /**
     * Calculates the weighted average fee coefficient for a list of orders.
     * This method is needed to determine the average fee coefficient across multiple orders,
     * taking into account the total price of each order to weight the coefficients appropriately.
     * This is useful for scenarios where the fee needs to be averaged based on the order's value,
     * ensuring that larger orders have a proportionally larger impact on the average.
     *
     * @param {Order[]} orders - The list of orders to calculate the weighted average fee coefficient for.
     * @param {(order: Order) => Promise<number>} retrieveFeeCoefficientForOrder - A function to retrieve the fee coefficient for an order.
     * @returns {Promise<number>} - The weighted average fee coefficient.
     */
    static async calculateWeightedAverageFeeCoefficientForOrders(orders, retrieveFeeCoefficientForOrder) {
        const orderTotals = orders.reduce((acc, order) => acc + (order.totalPrice ?? 0), 0);
        const getOrderWeight = (orderTotalPrice) => (orderTotals > 0 ? orderTotalPrice / orderTotals : 1 / orders.length);
        const feeCoefficients = await Promise.all(orders.map(async (order) => {
            // get fee coefficient based on fee and totalPrice - this takes discounts into account
            const feeCoefficient = order.price && order.totalPrice ? order.price / order.totalPrice : await retrieveFeeCoefficientForOrder(order);
            return feeCoefficient * getOrderWeight(order.totalPrice ?? 0);
        }));
        return feeCoefficients.reduce((acc, feeCoefficient) => {
            return acc + feeCoefficient;
        }, 0);
    }
    /**
     * Calculates the minimum price for a trip based on the base price and the trip's minimum price range.
     *
     * The formula is designed to introduce lower or higher minimums depending on the duration or distance of the trip.
     * This approach helps to avoid overpricing for short trips by gradually increasing the price based on the distance.
     *
     * @param {Object} params - The parameters for the calculation.
     * @param {number} params.tripMinPrice - The minimum price for the trip.
     * @param {number} params.tripMinPriceUpTo - The upper limit for the minimum price range.
     * @param {number} basePrice - The base price of the trip.
     * @returns {number} - The calculated minimum price.
     *
     * The calculation works as follows:
     * - If the base price is greater than or equal to `tripMinPriceUpTo`, the base price is returned.
     * - Otherwise, the price is calculated by gradually increasing from `tripMinPrice` to `tripMinPriceUpTo` based on the base price.
     * - If `tripMinPrice` equals `tripMinPriceUpTo`, the minimum price is fixed at `tripMinPrice`.
     */
    static calculateGradualMinPrice(params, basePrice) {
        // a bit of a defensive approach to ensure it works if some min prices are not set
        const tripMinimumPriceFrom = params.tripMinimumPriceFrom && params.tripMinimumPriceFrom > 0 ? params.tripMinimumPriceFrom : 0;
        let tripMinimumPriceUpTo = params.tripMinimumPriceUpTo && params.tripMinimumPriceUpTo > 0 ? params.tripMinimumPriceUpTo : 0;
        if (tripMinimumPriceFrom > tripMinimumPriceUpTo) {
            tripMinimumPriceUpTo = tripMinimumPriceFrom;
        }
        if (basePrice >= tripMinimumPriceUpTo) {
            return Math.round(basePrice);
        }
        const maxFlexibleMin = tripMinimumPriceUpTo - tripMinimumPriceFrom;
        return Math.round(tripMinimumPriceFrom + maxFlexibleMin * (basePrice / tripMinimumPriceUpTo));
    }
    /**
     * Calculates the minimum price for a roundtrip, adjusting for driver fees and a roundtrip bonus.
     *
     * For roundtrips, the minimum price might need to be increased to ensure driver compensation is sufficient.
     * This method calculates a roundtrip bonus when the driver's minimum expected fee exceeds the actual driver fee,
     * and applies this bonus to both the minimum price threshold values before calculating the gradual minimum price.
     *
     * The calculation works as follows:
     * - Calculate the driver's minimum expected fee by subtracting non-driver fees from the trip minimum price
     * - Calculate the roundtrip bonus by multiplying the driver's minimum fee by the roundtrip coefficient and subtracting the actual driver fee
     * - If the roundtrip bonus is positive, increase both threshold values by the bonus amount
     * - Apply the gradual minimum price calculation using the adjusted thresholds
     */
    static calculateGradularMinPriceForRoundrip({ tripMinimumPrice, amortizationFee, moneyForFuel, driverFee, roundtripMinimumCoefficient, tripMinimumPriceUpTo, sum, }) {
        const driverMinFee = tripMinimumPrice - amortizationFee - moneyForFuel;
        const roundtripBonus = Math.max(driverMinFee * roundtripMinimumCoefficient - driverFee, 0);
        return PriceCalculator.calculateGradualMinPrice({
            tripMinimumPriceFrom: tripMinimumPrice + roundtripBonus,
            tripMinimumPriceUpTo: tripMinimumPriceUpTo + roundtripBonus,
        }, sum);
    }
    /**
     * Calculates the base price from a calculated minimum price, performing the inverse operation of calculateGradualMinPrice.
     *
     * This method is useful when you have a calculated minimum price and need to determine what base price
     * would have resulted in that minimum price given the trip minimum price range.
     *
     * @param {Object} params - The parameters for the calculation.
     * @param {number} params.tripMinimumPriceFrom - The minimum price for the trip.
     * @param {number} params.tripMinimumPriceUpTo - The upper limit for the minimum price range.
     * @param {number} calculatedMinPrice - The calculated minimum price to reverse-engineer.
     * @returns {number} - The base price that would produce the given calculated minimum price.
     *
     * The calculation works as follows:
     * - If the calculated minimum price equals tripMinimumPriceFrom, returns 0 (since any base price below tripMinimumPriceUpTo would result in tripMinimumPriceFrom)
     * - If the calculated minimum price is greater than or equal to tripMinimumPriceUpTo, returns the calculated minimum price itself
     * - Otherwise, reverse-engineers the base price using the inverse of the gradual calculation formula
     */
    static calculateApproxBasePriceFromGradualMinPrice(params, calculatedMinPrice) {
        // Apply the same defensive approach as in calculateGradualMinPrice
        const tripMinimumPriceFrom = params.tripMinimumPriceFrom && params.tripMinimumPriceFrom > 0 ? params.tripMinimumPriceFrom : 0;
        let tripMinimumPriceUpTo = params.tripMinimumPriceUpTo && params.tripMinimumPriceUpTo > 0 ? params.tripMinimumPriceUpTo : 0;
        if (tripMinimumPriceFrom > tripMinimumPriceUpTo) {
            tripMinimumPriceUpTo = tripMinimumPriceFrom;
        }
        // If calculatedMinPrice is greater than or equal to tripMinimumPriceUpTo,
        // the base price equals the calculatedMinPrice
        if (calculatedMinPrice >= tripMinimumPriceUpTo) {
            return calculatedMinPrice;
        }
        // If calculatedMinPrice equals tripMinimumPriceFrom, the base price could be anywhere from 0 to tripMinimumPriceUpTo
        // We return 0 as the minimum possible base price that would result in tripMinimumPriceFrom
        if (calculatedMinPrice <= tripMinimumPriceFrom) {
            return 0;
        }
        // If tripMinimumPriceFrom equals tripMinimumPriceUpTo, any base price below this value would result in the fixed minimum
        if (tripMinimumPriceFrom === tripMinimumPriceUpTo) {
            return 0;
        }
        // Reverse the gradual calculation formula:
        // calculatedMinPrice = tripMinimumPriceFrom + maxFlexibleMin * (basePrice / tripMinimumPriceUpTo)
        // Solving for basePrice:
        // basePrice = ((calculatedMinPrice - tripMinimumPriceFrom) / maxFlexibleMin) * tripMinimumPriceUpTo
        const maxFlexibleMin = tripMinimumPriceUpTo - tripMinimumPriceFrom;
        return Math.floor(((calculatedMinPrice - tripMinimumPriceFrom) / maxFlexibleMin) * tripMinimumPriceUpTo);
    }
    static contentLocationVehiclesWaitingPrice(vehicleTypesPricesFees, vehicleTypes) {
        return vehicleTypes.reduce((sum, vehicleType) => {
            return sum + (vehicleTypesPricesFees.find((vtpf) => vtpf.vehicleType === vehicleType)?.driverWaitingPrice || 0);
        }, 0);
    }
    /**
     * Get avg driver price for comparison, etc.
     */
    static calculateAvgGuidePriceForVehicle(pricing, selectedVehicles) {
        return Math.round(pricing.vehicleTypeCosts
            .filter((vtc) => (selectedVehicles || []).includes(vtc.vehicleType))
            .reduce((acc, vtc) => acc + (vtc.driverPricePerHour || 0), 0) / selectedVehicles.length);
    }
    /**
     * Get avg driver price for comparison, etc.
     */
    static calculateAvgGuidePrice(pricing) {
        return Math.round(pricing.vehicleTypeCosts.reduce((acc, vtc) => acc + (vtc.driverPricePerHour || 0), 0) / pricing.vehicleTypeCosts.length);
    }
    /**
     * Get avg driver location price for comparison, etc.
     */
    static calculateAvgGuideLocationPrice(pricing) {
        return Math.round(pricing.vehicleTypeCosts.reduce((acc, vtc) => acc + (vtc.driverWaitingPricePerHour || 0), 0) /
            pricing.vehicleTypeCosts.length);
    }
    /**
     * Calculate location price for the driver for selected vehicles
     */
    static calculateLocationGuidePriceForVehicles(pricing, selectedVehicles) {
        return pricing.vehicleTypeCosts
            .filter((vtc) => (selectedVehicles || []).includes(vtc.vehicleType))
            .reduce((acc, vtc) => acc + (vtc.driverWaitingPricePerHour || 0), 0);
    }
    /**
     * Calculate location price for the driver for selected vehicle
     */
    static calculateLocationGuidePriceForVehicle(pricing, selectedVehicle) {
        return this.calculateLocationGuidePriceForVehicles(pricing, [selectedVehicle]);
    }
    /**
     * Calculate location price and fee for the driver for selected vehicle
     */
    static calculateDriverLocationPriceFeeForVehicle(pricing, waitingDuration, vehicleType) {
        const guideLocationPrice = PriceCalculator.calculateLocationGuidePriceForVehicle(pricing, vehicleType);
        const driverWaitingPrice = PriceCalculator.calculateWaitingPriceFromDuration(waitingDuration, guideLocationPrice);
        const feeCoefficient = (0, getVehicleTypeFeeCoefficient_1.getVehicleTypeFeeCoefficientOrDefault)(pricing, vehicleType);
        const driverWaitingFee = PriceCalculator.calculateFeeFromPrice(driverWaitingPrice, feeCoefficient);
        return { driverWaitingPrice, driverWaitingFee };
    }
}
exports.PriceCalculator = PriceCalculator;
//# sourceMappingURL=PriceCalculator.js.map